'use client';

import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { ChartHamburgerMenu } from './combined/ChartHamburgerMenu';
import { Typography } from '../ui-components/Typography/Typography';
import { theme } from '@/config/theme';

const Container = styled.div<{ $isFullscreen: boolean }>`
  overflow: visible;
  transition: all 0.3s ease;
  background: transparent;
  
  ${({ $isFullscreen }) =>
    $isFullscreen &&
    `
    height: 100vh;
    width: 100vw;
    display: flex;
    flex-direction: column;
    padding: 40px;
    background: ${theme.colors.white};
  `}
`;

const ChartHeader = styled.div`
  padding: 16px 20px 8px 20px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-shrink: 0;
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const ChartTitle = styled(Typography)`
  display: block;
  margin: 0;
  color: #3f3f3f;
  font-size: 11px;
`;

const ChartDescription = styled(Typography)`
  display: block;
  margin: 4px 0 0 0;
  color: #3f3f3f;
  font-size: 11px;
`;

const ChartContent = styled.div`
  flex: 1;
  
  > div {
    height: 100%;
  }
`;

export interface ChartContainerProps {
  title: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
  onZoomToggle?: (enabled: boolean) => void;
  initialZoomEnabled?: boolean;
  tableData?: Array<Record<string, string | number>>;
}

export const ChartContainer: React.FC<ChartContainerProps> = ({
  title,
  description,
  children,
  className,
  onZoomToggle,
  initialZoomEnabled = false,
  tableData = [],
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoomEnabled, setZoomEnabled] = useState(initialZoomEnabled);

  // Monitor fullscreen changes for THIS specific chart only
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!chartRef.current) return;
      
      const doc = document as Document & {
        webkitFullscreenElement?: Element;
        mozFullScreenElement?: Element;
        msFullscreenElement?: Element;
      };
      
      const fullscreenElement = 
        document.fullscreenElement ||
        doc.webkitFullscreenElement ||
        doc.mozFullScreenElement ||
        doc.msFullscreenElement;
      
      // Only update if THIS chart is the one in fullscreen (or exiting fullscreen)
      const isThisChartFullscreen = fullscreenElement === chartRef.current;
      const wasThisChartFullscreen = isFullscreen;
      
      if (isThisChartFullscreen !== wasThisChartFullscreen) {
        setIsFullscreen(isThisChartFullscreen);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, [isFullscreen]);

  const handleZoomToggle = (enabled: boolean) => {
    setZoomEnabled(enabled);
    if (onZoomToggle) {
      onZoomToggle(enabled);
    }
  };

  return (
    <Container ref={chartRef} $isFullscreen={isFullscreen} className={className}>
      <ChartHeader>
        <HeaderContent>
          <ChartTitle variant="label-uppercase" as="h3">{title}</ChartTitle>
          {description && <ChartDescription variant="label-uppercase" as="p">{description}</ChartDescription>}
        </HeaderContent>
        <ChartHamburgerMenu 
          chartTitle={title}
          tableData={tableData}
          chartContainerRef={chartRef}
          onZoomToggle={handleZoomToggle}
          initialZoomEnabled={zoomEnabled}
        />
      </ChartHeader>
      <ChartContent>
        {children}
      </ChartContent>
    </Container>
  );
};

